/* ============================================================================
  Custom bundle helpers for Dawn Cart Drawer - FIXED VERSION v8
  - Removes bundle (all items sharing _bundle_id)
  - Properly re-renders cart drawer sections with correct selectors
  - Shows proper empty cart content when cart is empty
  - Counts bundle as 1 item in cart count bubble (IMMEDIATELY on add)
  - FIXED: Fetches fresh cart data to ensure accurate bundle count
============================================================================ */

(() => {
  // prevent double init
  if (window.__M2_BUNDLE_HELPERS_V1__) return;
  window.__M2_BUNDLE_HELPERS_V1__ = true;

  // ✅ Helper: Calculate unique bundle count
  function getUniqueBundleCount(cartData) {
    if (!cartData || !cartData.items) return 0;
    
    const bundleIds = new Set();
    let regularItems = 0;
    
    cartData.items.forEach(item => {
      if (item.properties && item.properties._bundle_id) {
        bundleIds.add(item.properties._bundle_id);
      } else {
        regularItems += item.quantity;
      }
    });
    
    // Each bundle counts as 1, plus regular items
    const totalCount = bundleIds.size + regularItems;
    console.log('📊 Bundle Count Calculation:', {
      uniqueBundles: bundleIds.size,
      regularItems: regularItems,
      totalCount: totalCount,
      bundleIds: Array.from(bundleIds)
    });
    
    return totalCount;
  }

  // ✅ Update cart count bubble - IMPROVED with fresh cart fetch
  async function updateCartCount(cartData, forceImmediate = false) {
    // If we don't have cart data or want the freshest data, fetch it
    if (!cartData || forceImmediate) {
      try {
        const response = await fetch('/cart.js');
        if (response.ok) {
          cartData = await response.json();
          console.log('🔄 Fetched fresh cart data:', cartData);
        }
      } catch (err) {
        console.warn('Could not fetch fresh cart:', err);
        if (!cartData) return;
      }
    }
    
    const count = getUniqueBundleCount(cartData);
    
    console.log('🔢 Updating cart count to:', count, 'total items (bundles + regular)');
    
    // Main update function
    const doUpdate = () => {
      // Try multiple possible selectors for cart count (in priority order)
      const cartCountSelectors = [
        // Dawn theme common selectors
        '.cart-count-bubble span[aria-hidden="true"]',
        '#cart-icon-bubble span[aria-hidden="true"]',
        '.cart-count-bubble span:not([class])',
        '.cart-count-bubble span',
        '#cart-icon-bubble span',
        '.cart-count-bubble',
        '[data-cart-count]',
        // Additional fallbacks
        'a[href="/cart"] span',
        '.header__icon--cart span'
      ];
      
      let updated = false;
      
      // Try each selector
      for (const selector of cartCountSelectors) {
        const elements = document.querySelectorAll(selector);
        
        elements.forEach(element => {
          // Skip if this element contains other elements (we want the text node)
          if (element.children.length > 0 && selector.includes('span')) {
            return;
          }
          
          // Update the text content
          element.textContent = count;
          updated = true;
          
          console.log('✅ Updated cart count via selector:', selector);
          
          // Handle visibility of parent bubble
          const bubble = element.closest('.cart-count-bubble') || 
                        element.closest('#cart-icon-bubble') ||
                        element.closest('[class*="cart-count"]');
          
          if (bubble) {
            if (count === 0) {
              bubble.classList.add('cart-count-bubble--hidden');
              // bubble.style.display = 'none';
            } else {
              bubble.classList.remove('cart-count-bubble--hidden');
              // bubble.style.display = '';
            }
          }
        });
        
        if (updated) break;
      }
      
      if (!updated) {
        console.warn('⚠️ Could not find cart count element to update');
      }
      
      // Also update any data attributes
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.setAttribute('data-cart-count', count);
        if (el.textContent.trim().match(/^\d+$/)) {
          el.textContent = count;
        }
      });
    };

    // Execute immediately
    doUpdate();
    
    // If force immediate, also update after small delays to override any other updates
    if (forceImmediate) {
      setTimeout(doUpdate, 50);
      setTimeout(doUpdate, 150);
      setTimeout(doUpdate, 300);
    }
  }

  // ✅ Fixed Dawn drawer section renderer
  window.__M2_SAFE_RENDER_CART_SECTIONS__ = function (state, forceCountUpdate = false) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !state || !state.sections) {
      console.warn('Missing drawer or state.sections');
      return false;
    }

    // Use Dawn's standard sections to render
    const sectionsToRender = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];

    sectionsToRender.forEach((s) => {
      const html = state.sections[s.section];
      if (!html) {
        console.warn(`No HTML for section: ${s.section}`);
        return;
      }

      const container = document.getElementById(s.id);
      if (!container) {
        console.warn(`Container not found: ${s.id}`);
        return;
      }

      try {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newContent = parsed.querySelector(s.selector);
        
        if (!newContent) {
          console.warn(`Selector not found in parsed HTML: ${s.selector}`);
          return;
        }

        // For cart drawer, replace the entire inner HTML
        if (s.section === 'cart-drawer') {
          container.innerHTML = newContent.innerHTML;
          
          // Re-initialize cart drawer items if needed
          const cartDrawerItems = container.querySelector('cart-drawer-items');
          if (cartDrawerItems && typeof cartDrawerItems.connectedCallback === 'function') {
            cartDrawerItems.connectedCallback();
          }
        } else {
          // For other sections, replace content
          const currentContent = container.querySelector(s.selector);
          if (currentContent) {
            currentContent.innerHTML = newContent.innerHTML;
          } else {
            container.innerHTML = newContent.innerHTML;
          }
        }
      } catch (err) {
        console.error(`Error rendering section ${s.section}:`, err);
      }
    });

    // ✅ Update drawer empty/filled state based on item count
    if (state.item_count !== undefined) {
      const drawerElement = drawer.querySelector('.drawer');
      const cartDrawerItems = drawer.querySelector('cart-drawer-items');
      
      if (state.item_count === 0) {
        // Cart is empty - add empty class
        if (drawerElement) drawerElement.classList.add('is-empty');
        if (cartDrawerItems) cartDrawerItems.classList.add('is-empty');
        drawer.classList.add('is-empty');
      } else {
        // Cart has items - remove empty class
        if (drawerElement) drawerElement.classList.remove('is-empty');
        if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');
        drawer.classList.remove('is-empty');
      }
    }

    // ✅ CRITICAL: Update cart count with bundle logic after rendering
    // Always fetch fresh cart data when rendering sections
    updateCartCount(null, forceCountUpdate);

    return true;
  };

  // ✅ Remove bundle click handler
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-bundle-remove]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const bundleId = btn.getAttribute('data-bundle-remove');
    if (!bundleId) return;

    // Disable button to prevent double clicks
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Removing...';

    const drawer = document.querySelector('cart-drawer');

    try {
      // Get current cart
      const cart = await fetch('/cart.js').then((r) => {
        if (!r.ok) throw new Error('Failed to fetch cart');
        return r.json();
      });

      // Find all items in this bundle
      const updates = {};
      cart.items.forEach((item) => {
        if (item.properties && item.properties._bundle_id === bundleId) {
          updates[item.key] = 0;
        }
      });

      // If nothing to remove, just return
      if (!Object.keys(updates).length) {
        console.warn('No items found for bundle:', bundleId);
        btn.disabled = false;
        btn.textContent = originalText;
        return;
      }

      // Update cart
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json' 
        },
        body: JSON.stringify({
          updates,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.description || data?.message || 'Cart update failed');
      }

      // ✅ Re-render drawer with force count update
      if (drawer && window.__M2_SAFE_RENDER_CART_SECTIONS__) {
        const rendered = window.__M2_SAFE_RENDER_CART_SECTIONS__(data, true);
        
        if (rendered) {
          // Keep drawer open even if empty (to show empty state)
          if (typeof drawer.open === 'function') {
            setTimeout(() => {
              drawer.open();
            }, 100);
          }
          
          return;
        }
      }

      // Fallback: reload page
      console.warn('Falling back to page reload');
      window.location.reload();
      
    } catch (err) {
      console.error('Bundle remove error:', err);
      alert(err.message || 'Could not remove bundle. Please try again.');
      
      // Re-enable button
      btn.disabled = false;
      btn.textContent = originalText;
    }
  });

  // ✅ NEW: Intercept cart add/update responses to immediately update count
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    
    // Only intercept cart add/update requests
    if (typeof url === 'string' && (url.includes('/cart/add.js') || url.includes('/cart/update.js'))) {
      return originalFetch.apply(this, args).then(async (response) => {
        // Clone the response so we can read it
        const clonedResponse = response.clone();
        
        try {
          const data = await clonedResponse.json();
          
          // If this looks like a successful cart response, update count immediately
          if (data && (data.items || data.item_count !== undefined)) {
            console.log('🎯 Intercepted cart response, forcing count update with fresh data');
            // Force immediate update with fresh cart fetch
            setTimeout(() => updateCartCount(null, true), 100);
            setTimeout(() => updateCartCount(null, true), 300);
            setTimeout(() => updateCartCount(null, true), 500);
          }
        } catch (err) {
          // If parsing fails, just continue
          console.log('Could not parse cart response:', err);
        }
        
        return response;
      });
    }
    
    return originalFetch.apply(this, args);
  };

  // ✅ Make updateCartCount available globally for gift-box-builder
  window.__M2_UPDATE_CART_COUNT__ = updateCartCount;

  // ✅ Initialize cart count on page load
  async function initializeCartCount() {
    console.log('🚀 Initializing cart count on page load...');
    
    try {
      // Fetch current cart
      const response = await fetch('/cart.js');
      if (!response.ok) {
        console.warn('Could not fetch cart on page load');
        return;
      }
      
      const cartData = await response.json();
      console.log('📦 Cart data on page load:', cartData);
      
      // Update the count immediately
      const count = getUniqueBundleCount(cartData);
      
      // Try multiple possible selectors for cart count
      const cartCountSelectors = [
        '.cart-count-bubble span[aria-hidden="true"]',
        '#cart-icon-bubble span[aria-hidden="true"]',
        '.cart-count-bubble span:not([class])',
        '.cart-count-bubble span',
        '#cart-icon-bubble span',
        '.cart-count-bubble',
        '[data-cart-count]',
        'a[href="/cart"] span',
        '.header__icon--cart span'
      ];
      
      let updated = false;
      
      for (const selector of cartCountSelectors) {
        const elements = document.querySelectorAll(selector);
        
        elements.forEach(element => {
          if (element.children.length > 0 && selector.includes('span')) {
            return;
          }
          
          element.textContent = count;
          updated = true;
          
          console.log('✅ Initialized cart count on page load via selector:', selector);
          
          const bubble = element.closest('.cart-count-bubble') || 
                        element.closest('#cart-icon-bubble') ||
                        element.closest('[class*="cart-count"]');
          
          if (bubble) {
            if (count === 0) {
              bubble.classList.add('cart-count-bubble--hidden');
              // bubble.style.display = 'none';
            } else {
              bubble.classList.remove('cart-count-bubble--hidden');
              // bubble.style.display = '';
            }
          }
        });
        
        if (updated) break;
      }
      
      // Also update data attributes
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.setAttribute('data-cart-count', count);
        if (el.textContent.trim().match(/^\d+$/)) {
          el.textContent = count;
        }
      });
      
      console.log('✅ Cart count initialized on page load:', count);
      
    } catch (err) {
      console.error('Error initializing cart count:', err);
    }
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCartCount);
  } else {
    // DOM already loaded
    initializeCartCount();
  }

  console.log('🎁 Bundle cart helpers initialized v8 (with fresh cart fetch + page load init)');
})();

const el = document.getElementById('HeaderMenu-diy-candle-studio-book-a-private-party');

if (el) {
  el.classList.add('js-booking-popup');
}